import React, { useCallback, useMemo, useRef, useState } from "react";
import Plot from "./PlotlyChart";
import {
  getPlotlyLegendStyle,
  sortSpeciesForWeightLengthLegend,
} from "../../graphView/colors";
import "./wrVsLengthPlotly.css";
import { plotlyAxisBase, plotlyAxisTitle } from "./plotlyAxisTheme";
import { plotlyHorizontalLegendForTraces } from "./plotlyLegendLayout";
import {
  GraphV2FullscreenInlineRow,
  useGraphV2PlotHeight,
} from "../ChartFullscreenShell";
import { useSamplingPlotExportRegister } from "../../graphView/useSamplingPlotExportRegister";
import { identityReferenceLegendTrace } from "./plotlyReferenceLineLegend";

const CARD_W = 280;
const CARD_H = 320;

function buildSymmetricWeightTicks(graphData, unitValue, targetTickCount = 20) {
  const weights = (graphData || []).flatMap((d) => [
    Number(d?.standardWeight),
    Number(d?.actualWeight),
  ]).filter(Number.isFinite);

  if (!weights.length) {
    if (unitValue === "English") {
      return { ticks: [0, 0.25, 0.5, 0.75, 1], rangeStart: 0, rangeEnd: 1 };
    }
    return { ticks: [0, 100, 200, 300, 400], rangeStart: 0, rangeEnd: 400 };
  }

  const minV = Math.max(0, Math.min(...weights));
  const maxV = Math.max(...weights);
  const rawRange = Math.max(maxV - minV, unitValue === "English" ? 0.25 : 50);
  const roughStep = rawRange / Math.max(1, targetTickCount - 1);

  // Choose "nice" step sizes so labels are readable and consistent.
  const niceBases = unitValue === "English" ? [1, 2, 2.5, 5] : [1, 2, 5];
  const magnitude = Math.pow(10, Math.floor(Math.log10(roughStep)));
  let step = niceBases[niceBases.length - 1] * magnitude;
  for (const b of niceBases) {
    const candidate = b * magnitude;
    if (candidate >= roughStep - 1e-12) {
      step = candidate;
      break;
    }
  }

  // Snap bounds to step for symmetric-looking shared axes.
  const rangeStart = Math.max(0, Math.floor(minV / step) * step);
  const rangeEnd = Math.ceil(maxV / step) * step;

  const decimals = unitValue === "English" ? 4 : 0;
  const ticks = [];
  for (let v = rangeStart; v <= rangeEnd + step * 1e-6; v += step) {
    ticks.push(Number(v.toFixed(decimals)));
  }

  // Keep a denser axis for this chart; if sparse, reduce step once.
  const minTickCount = Math.max(10, Math.floor(targetTickCount * 0.7));
  if (ticks.length < minTickCount) {
    const finerStep = step / 2;
    const finerTicks = [];
    for (let v = rangeStart; v <= rangeEnd + finerStep * 1e-6; v += finerStep) {
      finerTicks.push(Number(v.toFixed(decimals)));
    }
    return {
      ticks: finerTicks,
      rangeStart,
      rangeEnd,
      minW: minV,
      maxW: maxV,
    };
  }

  return { ticks, rangeStart, rangeEnd, minW: minV, maxW: maxV };
}

function formatSignedFixed(n, decimals) {
  const s = n.toFixed(decimals);
  if (n > 0) return `+${s}`;
  return s;
}

export default function StandardVsActualChart({
  graphData,
  selectedUnit,
  selectedFish,
  isMobile,
}) {
  const wrapRef = useRef(null);
  const plotRef = useRef(null);
  useSamplingPlotExportRegister(plotRef);
  const [tip, setTip] = useState(null);

  const speciesList = useMemo(
    () =>
      sortSpeciesForWeightLengthLegend([...new Set(graphData.map((d) => d.species))]),
    [graphData]
  );

  const lengthUnit = selectedUnit.value === "English" ? "Inch" : "mm";
  const weightUnit = selectedUnit.value === "English" ? "Pound" : "gram";
  const standardTitle = `Standard Weight (${selectedUnit.value === "English" ? "Pound" : "gram"})`;
  const actualTitle = `Actual Weight (${selectedUnit.value === "English" ? "Pound" : "gram"})`;

  const { rangeStart, rangeEnd, ticks } = useMemo(() => {
    return buildSymmetricWeightTicks(graphData, selectedUnit.value, 20);
  }, [graphData, selectedUnit.value]);

  const traces = useMemo(() => {
    const t = [];
    speciesList.forEach((species) => {
      const rows = graphData.filter((d) => d.species === species);
      const samp = rows.filter((d) => !d.isStocking);
      const stock = rows.filter((d) => d.isStocking);
      const { color: lineColor, symbol: markSymbol } = getPlotlyLegendStyle(species, speciesList);
      const allPts = [...samp, ...stock].sort(
        (a, b) => Number(a.standardWeight) - Number(b.standardWeight)
      );
      if (allPts.length) {
        t.push({
          type: "scatter",
          mode: "markers",
          name: `${species} (${allPts.length})`,
          x: allPts.map((r) => r.standardWeight),
          y: allPts.map((r) => r.actualWeight),
          marker: {
            color: lineColor,
            size: 9,
            opacity: 0.85,
            symbol: markSymbol,
            line: { color: "white", width: 1 },
          },
          customdata: allPts.map((r) => [
            species,
            Number(r.length || 0),
            lineColor,
            !!r.isStocking,
          ]),
          hovertemplate: "<extra></extra>",
        });
      }
    });
    const refTrace = identityReferenceLegendTrace(rangeStart, rangeEnd);
    if (refTrace) t.push(refTrace);
    return t;
  }, [graphData, speciesList, rangeStart, rangeEnd]);

  const legendLayout = useMemo(
    () => plotlyHorizontalLegendForTraces(traces, isMobile),
    [traces, isMobile]
  );

  const plotHeight = useGraphV2PlotHeight({
    isMobile,
    mobileDefault: 500,
    desktopDefault: 700,
    legendExtra: legendLayout.extraHeight || 0,
  });

  const axisBundle = useMemo(
    () => ({
      range: [rangeStart, rangeEnd],
      tickmode: "array",
      tickvals: ticks,
      tickangle: isMobile ? -45 : 0,
      ...plotlyAxisBase(isMobile),
    }),
    [rangeStart, rangeEnd, ticks, isMobile]
  );

  const xAxisLayout = useMemo(
    () => ({
      ...axisBundle,
      title: {
        ...plotlyAxisTitle(standardTitle, isMobile),
        font: {
          ...plotlyAxisTitle(standardTitle, isMobile).font,
          size: isMobile ? 18 : 24,
        },
      },
      tickfont: {
        ...axisBundle.tickfont,
        size: isMobile ? 12 : 16,
      },
    }),
    [standardTitle, axisBundle, isMobile]
  );

  const yAxisLayout = useMemo(
    () => ({
      ...axisBundle,
      title: {
        ...plotlyAxisTitle(actualTitle, isMobile),
        font: {
          ...plotlyAxisTitle(actualTitle, isMobile).font,
          size: isMobile ? 18 : 24,
        },
      },
      tickfont: {
        ...axisBundle.tickfont,
        size: isMobile ? 12 : 16,
      },
    }),
    [actualTitle, axisBundle, isMobile]
  );

  const onHover = useCallback(
    (eventData) => {
      const pt = eventData?.points?.[0];
      const el = wrapRef.current;
      const evt = eventData?.event;
      if (
        !pt ||
        el == null ||
        typeof pt.x !== "number" ||
        typeof pt.y !== "number" ||
        !Number.isFinite(pt.x) ||
        !Number.isFinite(pt.y)
      ) {
        return;
      }
      const traceMode = pt.data?.mode || pt.fullData?.mode;
      if (traceMode !== "markers") {
        return;
      }
      const cd = pt.customdata;
      if (!Array.isArray(cd) || cd.length < 3) {
        return;
      }
      const isStocking = cd[3] === true;

      let cx;
      let cy;
      if (evt && typeof evt.clientX === "number" && typeof evt.clientY === "number") {
        cx = evt.clientX;
        cy = evt.clientY;
      } else if (pt.bbox) {
        cx = (pt.bbox.x0 + pt.bbox.x1) / 2;
        cy = pt.bbox.y0;
      } else {
        return;
      }

      const species = String(cd[0] ?? "");
      const length = Number(cd[1]);
      const seriesColor = String(cd[2] ?? "#059669");
      const standardWeight = pt.x;
      const actualWeight = pt.y;
      const diff = actualWeight - standardWeight;
      const pct = standardWeight > 0 ? (actualWeight / standardWeight - 1) * 100 : 0;
      const aboveStandard = actualWeight >= standardWeight;

      const diffClass =
        diff > 1e-6
          ? "wr-length-hover-card__value--diff-pos"
          : diff < -1e-6
            ? "wr-length-hover-card__value--diff-neg"
            : "wr-length-hover-card__value--diff-zero";

      const rect = el.getBoundingClientRect();
      const pad = 12;
      let left = cx - rect.left + pad;
      let top = cy - rect.top + pad;
      if (left + CARD_W > rect.width - 8) {
        left = cx - rect.left - CARD_W - pad;
      }
      if (top + CARD_H > rect.height - 8) {
        top = cy - rect.top - CARD_H - pad;
      }
      left = Math.max(8, Math.min(left, rect.width - CARD_W - 8));
      top = Math.max(8, Math.min(top, rect.height - CARD_H - 8));

      setTip({
        left,
        top,
        species,
        seriesColor,
        standardWeight,
        actualWeight,
        length,
        diff,
        pct,
        diffClass,
        aboveStandard,
        isStocking,
      });
    },
    []
  );

  const onUnhover = useCallback(() => setTip(null), []);

  return (
    <div
      ref={wrapRef}
      className="standard-vs-actual-plotly-wrap"
      style={{ position: "relative", width: "100%" }}
    >
      <GraphV2FullscreenInlineRow />
      <Plot
        ref={plotRef}
        data={traces}
        layout={{
          autosize: true,
          height: plotHeight,
          margin: { t: legendLayout.marginTop, r: 40, b: isMobile ? 100 : 90, l: 90 },
          paper_bgcolor: "white",
          plot_bgcolor: "white",
          xaxis: xAxisLayout,
          yaxis: yAxisLayout,
          legend: legendLayout.legend,
          hovermode: "closest",
          hoverdistance: 10,
        }}
        config={{ responsive: true, displaylogo: false }}
        style={{ width: "100%" }}
        useResizeHandler
        onHover={onHover}
        onUnhover={onUnhover}
      />
      {tip && (
        <div
          className={`wr-length-hover-card wr-length-hover-card--std-vs-actual${
            isMobile ? " wr-length-hover-card--mobile" : ""
          }`}
          style={{ left: tip.left, top: tip.top }}
          role="tooltip"
        >
          <div className="wr-length-hover-card__body">
            <p className="wr-length-hover-card__title wr-length-hover-card__label-with-swatch">
              <span
                className="wr-length-hover-card__dot-swatch"
                style={{ backgroundColor: tip.seriesColor }}
                aria-hidden
              />
              {tip.species}
              {tip.isStocking ? " · Stocked" : ""}
            </p>
            <div className="wr-length-hover-card__row">
              <span className="wr-length-hover-card__label wr-length-hover-card__label-with-swatch">
                <span className="wr-length-hover-card__dash-swatch" aria-hidden />
                Standard Weight:
              </span>
              <span className="wr-length-hover-card__value wr-length-hover-card__value--standard">
                {tip.standardWeight.toFixed(2)} {weightUnit}
              </span>
            </div>
            <div className="wr-length-hover-card__row">
              <span className="wr-length-hover-card__label wr-length-hover-card__label-with-swatch">
                <span
                  className="wr-length-hover-card__dot-swatch"
                  style={{ backgroundColor: tip.seriesColor }}
                  aria-hidden
                />
                Actual Weight:
              </span>
              <span className="wr-length-hover-card__value wr-length-hover-card__value--actual">
                {tip.actualWeight.toFixed(2)} {weightUnit}
              </span>
            </div>
            <div className="wr-length-hover-card__row">
              <span className="wr-length-hover-card__label">Length:</span>
              <span className="wr-length-hover-card__value wr-length-hover-card__value--length">
                {Number.isFinite(tip.length) ? `${tip.length.toFixed(2)} ${lengthUnit}` : "—"}
              </span>
            </div>
            <div className="wr-length-hover-card__subgrid">
              <div className="wr-length-hover-card__row">
                <span className="wr-length-hover-card__label">Difference:</span>
                <span className={`wr-length-hover-card__value ${tip.diffClass}`}>
                  {Number.isFinite(tip.diff)
                    ? `${formatSignedFixed(tip.diff, 2)} ${weightUnit}`
                    : "—"}
                </span>
              </div>
              <div className="wr-length-hover-card__row">
                <span className="wr-length-hover-card__label">Percentage:</span>
                <span className={`wr-length-hover-card__value ${tip.diffClass}`}>
                  {Number.isFinite(tip.pct) ? `${formatSignedFixed(tip.pct, 1)}%` : "—"}
                </span>
              </div>
            </div>
          </div>
          <div
            className={
              tip.aboveStandard
                ? "wr-length-hover-card__badge wr-length-hover-card__badge--ok"
                : "wr-length-hover-card__badge wr-length-hover-card__badge--low"
            }
          >
            {tip.aboveStandard
              ? "✓ At or above standard weight for this length"
              : "Below standard weight for this length"}
          </div>
        </div>
      )}
    </div>
  );
}
